import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Dashboard/Header/Header';
import Sidebar from '../../components/Dashboard/Sidebar/Sidebar';
import Breadcrumbs from '../../components/Common/Breadcrumbs/Breadcrumbs';
import Pagination from '../../components/Common/Pagination/Pagination';
import {
  Download,
  Search,
  Plus,
  Wallet,
  TrendingDown,
  Clock,
  CheckCircle2,
  Calendar,
  Loader2,
  Filter,
  X,
  MoreVertical,
  Pencil,
  Trash2,
  Eye,
  FileText,
  ChevronDown,
  ChevronUp,
  SlidersHorizontal as Filters,
  LayoutDashboard,
  XCircle
} from 'lucide-react';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import styles from './Expenses.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import Footer from '../../components/Common/Footer/Footer';
import NoData from '../../components/Common/NoData/NoData';
import { useLoading } from '../../context/LoadingContext';
import ExpenseDrawer from './ExpenseDrawer';

const MySwal = withReactContent(Swal);

const Expenses = () => {
  const navigate = useNavigate();
  const { loading, showLoader, hideLoader } = useLoading();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [expenses, setExpenses] = useState([]);
  const [pagination, setPagination] = useState({ total: 0 });
  const [showFilters, setShowFilters] = useState(false);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [openMenuIndex, setOpenMenuIndex] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const [filters, setFilters] = useState({
    project_id: '',
    category_id: '',
    status: ''
  });
  const [filterData, setFilterData] = useState({
    projects: [],
    categories: []
  });

  // Modal State for Quick Actions
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [targetExpenseId, setTargetExpenseId] = useState(null);

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const userPermissions = currentUser.permissions || [];
  const isAdmin = currentUser?.role?.toLowerCase() === 'admin';
  const canCreate = isAdmin || userPermissions.includes('create expenses');
  const canEdit = isAdmin || userPermissions.includes('edit expenses');
  const canDelete = isAdmin || userPermissions.includes('delete expenses');

  const [metricsData, setMetricsData] = useState({
    total_approved: 0,
    this_month_approved_expenses: 0,
    total_pending_expenses: 0,
    this_month_pending_expenses: 0,
    currency: '₹'
  });

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    fetchExpenses();
  }, [currentPage, itemsPerPage, searchTerm, filters]);

  useEffect(() => {
    fetchFilterData();
    fetchMetrics();
  }, []);

  const fetchFilterData = async () => {
    try {
      const [projRes, catRes] = await Promise.all([
        api.get('/projects-dropdown'),
        api.get('/expense-categories-dropdown')
      ]);
      setFilterData({
        projects: projRes.data.data || [],
        categories: catRes.data.data || []
      });
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilters({
      project_id: '',
      category_id: '',
      status: ''
    });
    setCurrentPage(1);
  };

  const hasActiveFilters = filters.project_id || filters.category_id || filters.status;

  const fetchExpenses = async () => {
    showLoader();
    try {
      const response = await api.get('/expenses', {
        params: {
          page: currentPage,
          per_page: itemsPerPage,
          search: searchTerm,
          ...filters
        }
      });
      if (response.data.success) {
        setExpenses(response.data.data.data);
        setPagination({
          total: response.data.data.total,
          current_page: response.data.data.current_page,
          per_page: response.data.data.per_page
        });
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      toast.error('Failed to load expenses');
    } finally {
      hideLoader();
    }
  };

  const fetchMetrics = async () => {
    setSummaryLoading(true);
    try {
      const response = await api.get('/total-expenses');
      if (response.data.success) {
        setMetricsData(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
    } finally {
      setSummaryLoading(false);
    }
  };

  const openActionModal = (expense, action) => {
    setTargetExpenseId(expense.id);
    setModalAction(action);
    setReason('');
    setIsModalOpen(true);
    setOpenMenuIndex(null);
    fetchExpenseHistory(expense.id); // Fetch history for modal context
  };

  const [modalHistories, setModalHistories] = useState([]);

  const fetchExpenseHistory = async (id) => {
    try {
      const response = await api.get(`/expenses/${id}/history`);
      if (response.data.success) {
        setModalHistories(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();

    setIsSubmitting(true);
    try {
      const response = await api.post(`/expenses/${targetExpenseId}/${modalAction}`, { reason });
      if (response.data.success) {
        toast.success(`Expense ${modalAction === 'approve' ? 'approved' : 'rejected'} successfully`);
        setIsModalOpen(false);
        fetchExpenses();
        fetchMetrics();
      }
    } catch (error) {
      console.error(`Error ${modalAction}ing expense:`, error);
      toast.error(error.response?.data?.message || `Failed to ${modalAction} expense`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleMenu = (e, index) => {
    e.stopPropagation();
    if (openMenuIndex === index) {
      setOpenMenuIndex(null);
    } else {
      const rect = e.currentTarget.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const menuHeight = 200; // Estimated height of the dropdown

      let top = rect.bottom + window.scrollY;

      // If there's not enough space below, show it above the button
      if (rect.bottom + menuHeight > windowHeight) {
        top = rect.top + window.scrollY - menuHeight + 40; // 40 is button height
      }

      setMenuPosition({
        top: top,
        left: rect.left - 120
      });
      setOpenMenuIndex(index);
    }
  };

  const handleDelete = async (expense) => {
    setOpenMenuIndex(null);

    MySwal.fire({
      showConfirmButton: true,
      showCancelButton: true,
      confirmButtonText: '<div style="display: flex; align-items: center; justify-content: center; gap: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"></path><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg> Delete Expense</div>',
      cancelButtonText: 'Cancel',
      buttonsStyling: false,
      customClass: {
        popup: 'custom-swal-popup',
        actions: 'custom-swal-actions',
        confirmButton: 'custom-swal-confirm',
        cancelButton: 'custom-swal-cancel',
      },
      html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          @media (max-width: 480px) {
            .custom-swal-popup {
              width: calc(100vw - 32px) !important;
              padding: 24px 16px 20px 16px !important;
            }
            .custom-swal-actions {
              flex-direction: column !important;
              gap: 8px !important;
            }
            .custom-swal-confirm,
            .custom-swal-cancel {
              width: 100% !important;
              justify-content: center !important;
            }
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background-color: #dc2626 !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-confirm:hover {
            background-color: #b91c1c !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            transition: all 0.2s !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f8fafc !important;
          }
          .custom-icon-wrapper {
            width: 64px;
            height: 64px;
            background-color: #fee2e2;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 20px;
          }
        </style>
        <div class="custom-icon-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"></path>
            <line x1="12" y1="9" x2="12" y2="13"></line>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </div>
        <h2 style="font-size: 20px; font-weight: 700; color: #0f172a; margin: 0 0 12px 0;">Delete Expense?</h2>
        <p style="margin: 0 0 24px 0; color: #475569; font-size: 14px; line-height: 1.5;">
          Are you sure you want to delete this expense record for <strong>${expense.description || 'N/A'}</strong>?<br/>
          This action will permanently remove the record and update financial metrics.
        </p>
      `
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await api.delete(`/expenses/${expense.id}`);
          if (response.data.success) {
            toast.success('Expense deleted successfully');
            fetchExpenses();
            fetchMetrics();
          }
        } catch (error) {
          console.error('Error deleting expense:', error);
          toast.error(error.response?.data?.message || 'Failed to delete expense');
        }
      }
    });
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuIndex(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const metrics = [
    {
      label: 'Total Expenses',
      value: `${metricsData.currency}${parseFloat(metricsData.total_approved).toLocaleString('en-IN')}`,
      icon: <Wallet size={20} />,
      theme: 'orange'
    },
    {
      label: 'This Month',
      value: `${metricsData.currency}${parseFloat(metricsData.this_month_approved_expenses).toLocaleString('en-IN')}`,
      icon: <TrendingDown size={20} />,
      theme: 'blue'
    },
    {
      label: 'Pending Approval',
      value: metricsData.total_pending_expenses.toString(),
      icon: <Clock size={20} />,
      theme: 'purple'
    },
    {
      label: 'This Month Pending',
      value: metricsData.this_month_pending_expenses.toString(),
      icon: <CheckCircle2 size={20} />,
      theme: 'green'
    }
  ];

  return (
    <div className={`${styles['expenses-container']} ${isSidebarOpen ? styles['sidebar-open'] : ''}`}>
      <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      {isSidebarOpen && <div className={styles['sidebar-overlay']} onClick={() => setIsSidebarOpen(false)}></div>}

      <div className={styles['content-wrapper']}>
        <Header onMenuClick={toggleSidebar} />

        <main className={styles['main-content']}>
          <Breadcrumbs />

          <div className={styles['page-header']}>
            <div className={styles['header-title-section']}>
              <h1>Expenses</h1>
              <p>Track and manage all project-related and general business expenses.</p>
            </div>
            <div className={styles['header-actions-top']}>
              <button className={styles['control-btn']}>
                <Download size={18} />
                <span>Export</span>
              </button>
              {canCreate && (
                <button
                  className={styles['add-btn']}
                  onClick={() => {
                    setSelectedExpense(null);
                    setIsDrawerOpen(true);
                  }}
                >
                  <Plus size={20} />
                  <span>Record Expense</span>
                </button>
              )}
            </div>
          </div>

          <div className={styles['summary-collapsible']}>
            <button
              className={styles['summary-toggle']}
              onClick={() => setIsSummaryOpen(!isSummaryOpen)}
            >
              <div className={styles['summary-toggle-left']}>
                <LayoutDashboard size={20} style={{ color: '#f97316' }} />
                <div className={styles['summary-toggle-text']}>
                  <span className={styles['summary-toggle-title']}>Summary</span>
                  <span className={styles['summary-toggle-hint']}>
                    {metrics[0].value} total expenses · {metrics[2].value} pending
                  </span>
                </div>
              </div>
              {isSummaryOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </button>

            <div className={`${styles['summary-metrics-row']} ${isSummaryOpen ? styles['summary-expanded'] : styles['summary-collapsed']}`}>
              {metrics.map((metric, index) => (
                <div key={index} className={`${styles['metric-card']} ${styles[metric.theme]} ${summaryLoading ? styles['loading'] : ''}`}>
                  <div className={styles['metric-icon-box']}>
                    {metric.icon}
                  </div>
                  <div className={styles['metric-info']}>
                    <span className={styles['metric-label']}>{metric.label}</span>
                    <span className={styles['metric-value']}>{metric.value}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={styles['table-controls']}>
            <div className={styles['search-wrapper']}>
              <Search className={styles['search-icon']} size={18} />
              <input
                type="text"
                placeholder="Search by description or reference..."
                className={styles['search-input']}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className={styles['filter-actions']}>
              <div className={styles['per-page-select-wrapper']}>
                <CustomSelect
                  isSearchable={false}
                  backgroundColor="white"
                  value={{ value: itemsPerPage, label: `${itemsPerPage} per page` }}
                  onChange={(option) => { setItemsPerPage(Number(option.value)); setCurrentPage(1); }}
                  options={[
                    { value: 10, label: '10 per page' },
                    { value: 25, label: '25 per page' },
                    { value: 50, label: '50 per page' },
                    { value: 100, label: '100 per page' }
                  ]}
                />
              </div>
              <button
                className={`${styles['filter-btn']} ${showFilters ? styles['active'] : ''}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filters size={18} />
                <span>Filters</span>
                {hasActiveFilters && <span className={styles['filter-badge']}></span>}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={styles['filters-panel']}>
              <div className={styles['filters-header']}>
                <div className={styles['filters-title-group']}>
                  <h3>Filter Expenses</h3>
                  <p>Filter records by project, category or status.</p>
                </div>
                <button className={styles['close-icon-btn']} onClick={() => setShowFilters(false)}>
                  <X size={18} />
                </button>
              </div>
              <div className={styles['filters-grid']}>
                <div className={styles['filter-group']}>
                  <label>Project</label>
                  <CustomSelect
                    value={filters.project_id ? {
                      value: filters.project_id,
                      label: filterData.projects.find(d => d.id === filters.project_id)?.project_name
                    } : null}
                    onChange={(opt) => handleFilterChange('project_id', opt ? opt.value : '')}
                    options={filterData.projects.map(d => ({ value: d.id, label: d.project_name }))}
                    placeholder="All Projects"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Category</label>
                  <CustomSelect
                    value={filters.category_id ? {
                      value: filters.category_id,
                      label: filterData.categories.find(c => c.id === filters.category_id)?.name
                    } : null}
                    onChange={(opt) => handleFilterChange('category_id', opt ? opt.value : '')}
                    options={filterData.categories.map(c => ({ value: c.id, label: c.name }))}
                    placeholder="All Categories"
                    isClearable={true}
                  />
                </div>
                <div className={styles['filter-group']}>
                  <label>Status</label>
                  <CustomSelect
                    value={filters.status ? { value: filters.status, label: filters.status.charAt(0).toUpperCase() + filters.status.slice(1) } : null}
                    onChange={(opt) => handleFilterChange('status', opt ? opt.value : '')}
                    options={[
                      { value: 'pending', label: 'Pending' },
                      { value: 'approved', label: 'Approved' },
                      { value: 'rejected', label: 'Rejected' }
                    ]}
                    placeholder="All Statuses"
                    isClearable={true}
                  />
                </div>
              </div>
              <div className={styles['filters-footer']}>
                <button className={styles['reset-btn']} onClick={resetFilters}>Reset</button>
                <button className={styles['close-btn']} onClick={() => setShowFilters(false)}>Close Filters</button>
              </div>
            </div>
          )}

          <div className={styles['data-view-container']}>
            {/* Desktop View */}
            <div className={styles['table-wrapper']}>
              <table className={styles['expense-table']}>
                <thead>
                  <tr>
                    <th>DATE</th>
                    <th>CATEGORY</th>
                    <th>DESCRIPTION</th>
                    <th>PROJECT</th>
                    <th>EMPLOYEE</th>
                    <th>AMOUNT</th>
                    <th>STATUS</th>
                    <th className={styles['actions-cell']}>ACTIONS</th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.length === 0 ? (
                    !loading && <NoData message="No expenses found." colSpan={8} isTable={true} />
                  ) : expenses.map((expense, index) => (
                    <tr key={index}>
                      <td>{new Date(expense.expense_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                      <td>
                        <span className={styles['category-badge']}>{expense.category?.category_name || 'Uncategorized'}</span>
                      </td>
                      <td className={styles['description-cell']}>{expense.description}</td>
                      <td className={styles['project-name']}>{expense.project?.project_name || 'General Business'}</td>
                      <td>{expense.employee?.employee_name || 'N/A'}</td>
                      <td className={styles['amount-cell']}>{parseFloat(expense.amount).toLocaleString('en-IN')}</td>
                      <td>
                        <span className={`${styles['status-pill']} ${styles[expense.status?.toLowerCase()] || styles['pending']}`}>
                          {expense.status || 'Pending'}
                        </span>
                      </td>
                      <td className={styles['actions-cell']}>
                        <div className={styles['action-wrapper']}>
                          <button
                            className={`${styles['action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                            onClick={(e) => toggleMenu(e, index)}
                          >
                            <MoreVertical size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile View - Touch Optimized Cards */}
            <div className={styles['expense-cards']}>
              {expenses.length === 0 ? (
                !loading && <NoData message="No expenses found." isTable={false} />
              ) : expenses.map((expense, index) => (
                <div key={index} className={styles['expense-card']}>
                  <div className={styles['card-main']}>
                    <div className={styles['card-info']}>
                      <span className={styles['card-category']}>{expense.category?.category_name || 'General Expense'}</span>
                      <span className={styles['card-amount']}>{parseFloat(expense.amount).toLocaleString('en-IN')}</span>
                    </div>
                    <button
                      className={`${styles['mobile-action-btn']} ${openMenuIndex === index ? styles['active'] : ''}`}
                      onClick={(e) => toggleMenu(e, index)}
                    >
                      <MoreVertical size={18} />
                    </button>
                  </div>

                  {expense.description && (
                    <div className={styles['card-description']}>
                      {expense.description}
                    </div>
                  )}

                  <div className={styles['card-details']}>
                    <div className={styles['detail-item']} style={{ gridColumn: 'span 2' }}>
                      <span className={styles['detail-label']}>Project</span>
                      <span className={styles['detail-value']}>{expense.project?.project_name || 'General Business'}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['detail-label']}>Incurred By</span>
                      <span className={styles['detail-value']}>{expense.employee?.employee_name || 'N/A'}</span>
                    </div>
                    <div className={styles['detail-item']}>
                      <span className={styles['detail-label']}>Entered by</span>
                      <span className={styles['detail-value']}>{expense.creator?.name || 'System'}</span>
                    </div>
                  </div>

                  <div className={styles['card-footer']}>
                    <div className={styles['footer-left']}>
                      <Calendar size={14} />
                      <span>{new Date(expense.expense_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                    </div>
                    <span className={`${styles['status-pill']} ${styles[expense.status?.toLowerCase()] || styles['pending']}`}>
                      {expense.status || 'Pending'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <div className={styles['desktop-pagination']}>
              <Pagination
                currentPage={currentPage}
                totalItems={pagination.total}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>

          <div className={styles['mobile-pagination']}>
            <Pagination
              currentPage={currentPage}
              totalItems={pagination.total}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
            />
          </div>

          <ExpenseDrawer
            isOpen={isDrawerOpen}
            onClose={() => {
              setIsDrawerOpen(false);
              setSelectedExpense(null);
            }}
            onSuccess={() => {
              fetchExpenses();
              fetchFilterData(); // Refresh dropdowns if needed
            }}
            expense={selectedExpense}
          />

          {openMenuIndex !== null && (
            <div
              className={[styles['action-dropdown'], styles['shadow-premium']].join(' ')}
              style={{
                position: 'fixed',
                top: `${menuPosition.top}px`,
                left: `${menuPosition.left}px`,
                zIndex: 9999
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={styles['dropdown-item']}
                onClick={() => {
                  navigate(`/expenses/view/${expenses[openMenuIndex]?.id}`);
                  setOpenMenuIndex(null);
                }}
              >
                <Eye size={16} />
                <span>View Details</span>
              </button>
              {canEdit && (
                <button
                  className={styles['dropdown-item']}
                  onClick={() => {
                    setSelectedExpense(expenses[openMenuIndex]);
                    setIsDrawerOpen(true);
                    setOpenMenuIndex(null);
                  }}
                >
                  <Pencil size={16} />
                  <span>Edit Expense</span>
                </button>
              )}
              {isAdmin && (
                <>
                  <button
                    className={styles['dropdown-item']}
                    onClick={() => openActionModal(expenses[openMenuIndex], 'approve')}
                  >
                    <CheckCircle2 size={16} style={{ color: '#16a34a' }} />
                    <span>Approve</span>
                  </button>
                  <button
                    className={styles['dropdown-item']}
                    onClick={() => openActionModal(expenses[openMenuIndex], 'reject')}
                  >
                    <XCircle size={16} style={{ color: '#dc2626' }} />
                    <span>Reject</span>
                  </button>
                </>
              )}
              {canDelete && (
                <button
                  className={[styles['dropdown-item'], styles['deselect']].join(' ')}
                  onClick={() => handleDelete(expenses[openMenuIndex])}
                >
                  <Trash2 size={16} />
                  <span>Delete</span>
                </button>
              )}
            </div>
          )}

          <Footer />
        </main>
      </div>

      {/* Action Modal */}
      {isModalOpen && (
        <div className={styles['modal-overlay']} onClick={() => setIsModalOpen(false)}>
          <div className={styles['modal-content']} onClick={(e) => e.stopPropagation()}>
            <div className={styles['modal-header']}>
              <div className={styles['modal-title-wrapper']}>
                <div className={`${styles['modal-icon']} ${modalAction === 'approve' ? styles['bg-approve'] : styles['bg-reject']}`}>
                  {modalAction === 'approve' ? <CheckCircle2 size={24} /> : <XCircle size={24} />}
                </div>
                <div>
                  <h3>{modalAction === 'approve' ? 'Approve Expense' : 'Reject Expense'}</h3>
                  <p>Provide a reason for this decision</p>
                </div>
              </div>
              <button className={styles['modal-close']} onClick={() => setIsModalOpen(false)}>
                <X size={20} />
              </button>
            </div>

            <div className={styles['modal-body-wrapper']}>
              {modalHistories.length > 0 && (
                <div className={styles['modal-history-section']}>
                  <h4>Previous History</h4>
                  <div className={styles['m-hist-table']}>
                    <div className={styles['m-hist-thead']}>
                      <div className={styles['m-hist-th']}>DATE</div>
                      <div className={styles['m-hist-th']}>USER</div>
                      <div className={styles['m-hist-th']}>STATUS</div>
                      <div className={styles['m-hist-th']}>REASON</div>
                    </div>
                    <div className={styles['m-hist-tbody']}>
                      {modalHistories.map((item, idx) => (
                        <div key={idx} className={styles['m-hist-tr']}>
                          <div className={styles['m-hist-td']} data-label="DATE">{new Date(item.created_at).toLocaleDateString()}</div>
                          <div className={styles['m-hist-td']} data-label="USER">{item.changed_by?.name}</div>
                          <div className={styles['m-hist-td']} data-label="STATUS">
                            <span className={`${styles['m-hist-badge']} ${styles[item.new_status]}`}>{item.new_status}</span>
                          </div>
                          <div className={styles['m-hist-td']} data-label="REASON">{item.reason}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleActionSubmit} className={styles['modal-form']}>
                <div className={styles['modal-body-padding']}>
                  <div className={styles['modal-form-group']}>
                    <label>
                      <FileText size={16} />
                      Reason / Comments <span style={{ color: 'red' }}>*</span>
                    </label>
                    <textarea
                      placeholder={`Enter reason for ${modalAction === 'approve' ? 'approval' : 'rejection'}...`}
                      rows={4}
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </div>
                </div>
                <div className={styles['modal-footer']}>
                  <button type="button" className={styles['btn-modal-secondary']} onClick={() => setIsModalOpen(false)}>
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`${styles['btn-modal-primary']} ${modalAction === 'approve' ? styles['btn-approve'] : styles['btn-reject']}`}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <Loader2 size={18} className={styles['spinner']} />
                    ) : (
                      modalAction === 'approve' ? <CheckCircle2 size={18} /> : <XCircle size={18} />
                    )}
                    <span>{isSubmitting ? 'Processing...' : modalAction === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Expenses;
